import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  signOut,
  sendPasswordResetEmail,
  confirmPasswordReset,
  signInWithPhoneNumber,
  PhoneAuthProvider,
  linkWithCredential,
  RecaptchaVerifier,
  ConfirmationResult,
  UserCredential,
  User,
} from 'firebase/auth';
import { initializeFirebase } from '../config/firebase';

export interface FirebaseAuthError {
  code: string;
  message: string;
}

export class FirebaseAuthService {
  private static instance: FirebaseAuthService;
  private auth: any = null;
  private isSigningIn: boolean = false;

  private constructor() {}

  public static getInstance(): FirebaseAuthService {
    if (!FirebaseAuthService.instance) {
      FirebaseAuthService.instance = new FirebaseAuthService();
    }
    return FirebaseAuthService.instance;
  }

  private async ensureInitialized() {
    if (!this.auth) {
      const { auth } = await initializeFirebase();
      this.auth = auth;
    }
  }

  async createUser(email: string, password: string): Promise<UserCredential> {
    await this.ensureInitialized();
    try {
      return await createUserWithEmailAndPassword(this.auth, email, password);
    } catch (error: unknown) {
      const errorObj = error as { code?: string; message?: string };
      const firebaseError: FirebaseAuthError = {
        code: errorObj.code || 'unknown',
        message: errorObj.message || 'An unknown error occurred',
      };
      throw firebaseError;
    }
  }

  async signIn(email: string, password: string): Promise<UserCredential> {
    await this.ensureInitialized();
    try {
      return await signInWithEmailAndPassword(this.auth, email, password);
    } catch (error: unknown) {
      const errorObj = error as { code?: string; message?: string };
      const firebaseError: FirebaseAuthError = {
        code: errorObj.code || 'unknown',
        message: errorObj.message || 'An unknown error occurred',
      };
      throw firebaseError;
    }
  }

  async signInWithGoogle(): Promise<UserCredential> {
    await this.ensureInitialized();
    
    // Prevent multiple concurrent sign-in attempts
    if (this.isSigningIn) {
      throw {
        code: 'auth/cancelled-popup-request',
        message: 'Another sign-in request is already in progress'
      };
    }
    
    try {
      this.isSigningIn = true;
      const provider = new GoogleAuthProvider();
      
      // Configure the provider for better UX
      provider.setCustomParameters({
        prompt: 'select_account'
      });
      
      const result = await signInWithPopup(this.auth, provider);
      return result;
    } catch (error: unknown) {
      const errorObj = error as { code?: string; message?: string };
      // Handle cancelled popup request specifically
      if (errorObj.code === 'auth/cancelled-popup-request') {
        console.log('Previous popup request cancelled, this is expected');
      }
      
      const firebaseError: FirebaseAuthError = {
        code: errorObj.code || 'unknown',
        message: errorObj.message || 'An unknown error occurred',
      };
      throw firebaseError;
    } finally {
      this.isSigningIn = false;
    }
  }

  async signOut(): Promise<void> {
    await this.ensureInitialized();
    try {
      await signOut(this.auth);
    } catch (error: unknown) {
      const errorObj = error as { code?: string; message?: string };
      const firebaseError: FirebaseAuthError = {
        code: errorObj.code || 'unknown',
        message: errorObj.message || 'An unknown error occurred',
      };
      throw firebaseError;
    }
  }

  async resetPassword(email: string): Promise<void> {
    await this.ensureInitialized();
    try {
      await sendPasswordResetEmail(this.auth, email);
    } catch (error: unknown) {
      const errorObj = error as { code?: string; message?: string };
      const firebaseError: FirebaseAuthError = {
        code: errorObj.code || 'unknown',
        message: errorObj.message || 'An unknown error occurred',
      };
      throw firebaseError;
    }
  }

  async confirmPasswordReset(code: string, newPassword: string): Promise<void> {
    await this.ensureInitialized();
    try {
      await confirmPasswordReset(this.auth, code, newPassword);
    } catch (error: unknown) {
      const errorObj = error as { code?: string; message?: string };
      const firebaseError: FirebaseAuthError = {
        code: errorObj.code || 'unknown',
        message: errorObj.message || 'An unknown error occurred',
      };
      throw firebaseError;
    }
  }

  getCurrentUser(): User | null {
    if (!this.auth) {
      console.warn('Firebase auth not initialized yet');
      return null;
    }
    const user = this.auth.currentUser;
    console.log('🔍 getCurrentUser called, auth initialized:', !!this.auth, 'current user:', user ? user.email : 'null');
    return user;
  }

  async onAuthStateChanged(callback: (user: User | null) => void): Promise<() => void> {
    await this.ensureInitialized();
    return this.auth.onAuthStateChanged(callback);
  }

  async sendPhoneCode(phoneNumber: string, recaptchaVerifier: RecaptchaVerifier): Promise<ConfirmationResult> {
    await this.ensureInitialized();
    try {
      return await signInWithPhoneNumber(this.auth, phoneNumber, recaptchaVerifier);
    } catch (error: unknown) {
      const errorObj = error as { code?: string; message?: string };
      throw { code: errorObj.code || 'unknown', message: errorObj.message || 'Failed to send SMS' };
    }
  }

  async linkPhone(user: User, verificationId: string, smsCode: string): Promise<UserCredential> {
    try {
      const credential = PhoneAuthProvider.credential(verificationId, smsCode);
      return await linkWithCredential(user, credential);
    } catch (error: unknown) {
      const errorObj = error as { code?: string; message?: string };
      throw { code: errorObj.code || 'unknown', message: errorObj.message || 'Failed to link phone' };
    }
  }
}

export const firebaseAuthService = FirebaseAuthService.getInstance();
